import { Injectable, inject, signal, computed } from '@angular/core';
import { BizEntityTypeCategory, IBizEntity, LoggerService } from '@optima-stride-ai/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NavigationStateService } from './navigation-state.service';
import { FileHandlerService } from './file-handler.service';

/**
 * Viewer state interface
 */
export interface ViewerState {
  isOpen: boolean;
  category: BizEntityTypeCategory | null;
  fileId: string | null;
  parentId: string | null;
}

/**
 * Service responsible for managing viewer lifecycle and state.
 * Handles opening, closing, and navigation between different file viewers.
 * 
 * @responsibility
 * - Track current viewer state
 * - Manage viewer opening/closing
 * - Handle viewer navigation
 * - Prevent viewer state conflicts
 * - Coordinate with navigation state
 */
@Injectable({
  providedIn: 'root'
})
export class ViewerManagerService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly navigationStateService = inject(NavigationStateService);
  private readonly fileHandlerService = inject(FileHandlerService);
  private readonly logger = inject(LoggerService);

  // Viewer state signals
  private readonly _isViewerOpen = signal<boolean>(false);
  private readonly _currentCategory = signal<BizEntityTypeCategory | null>(null);
  private readonly _currentFileId = signal<string | null>(null);
  private readonly _currentParentId = signal<string | null>(null);

  // Public readonly state
  readonly isViewerOpen = this._isViewerOpen.asReadonly();
  readonly currentCategory = this._currentCategory.asReadonly();
  readonly currentFileId = this._currentFileId.asReadonly();
  readonly currentParentId = this._currentParentId.asReadonly();

  // Computed viewer state
  readonly viewerState = computed<ViewerState>(() => ({
    isOpen: this._isViewerOpen(),
    category: this._currentCategory(),
    fileId: this._currentFileId(),
    parentId: this._currentParentId()
  }));

  // Flag to prevent navigation interference during viewer operations
  private isOpeningViewer = false;

  /**
   * Open a file in the appropriate viewer
   * 
   * @param file - The file entity to open
   * @returns true if viewer opened successfully, false otherwise
   */
  openViewer(file: IBizEntity): boolean {
    this.logger.debug(`🔍 Attempting to open viewer for file: ${file?.name || 'unknown'} (ID: ${file?.id})`);
    if (!file || !file.id) {
      this.logger.error(`❌ Cannot open viewer: invalid file - ${file?.name || 'undefined'}`);
      return false;
    }

    // Check if this is a navigable container (folder)
    if (this.fileHandlerService.isNavigableContainer(file)) {
      this.logger.debug('📁 Navigable container detected, not opening viewer');
      return false;
    }

    // Set flag IMMEDIATELY to prevent navigation interference
    // This must happen BEFORE any other operations
    this.isOpeningViewer = true;
    
    // Get the appropriate category from FileHandlerService
    const category = this.fileHandlerService.openFile(file, file.parentId || undefined);
    
    if (!category) {
      this.logger.warn(`⚠️ No viewer available for this file type: ${file.type || 'unknown'}`);
      // Clear flag if we're not actually opening a viewer
      this.isOpeningViewer = false;
      return false;
    }

    try {
      // Update viewer state
      this._currentFileId.set(file.id);
      this._currentCategory.set(category);
      this._currentParentId.set(file.parentId || null);
      this._isViewerOpen.set(true);

      this.logger.info(`📂 Opening viewer for ${file.name} (${category})`);
      
      return true;
    } finally {
      // Clear flag after a delay to allow Angular change detection and template updates
      setTimeout(() => {
        this.isOpeningViewer = false;
        this.logger.debug('🔓 Viewer opening flag cleared');
      }, 300);
    }
  }

  /**
   * Close the current viewer and return to explorer view
   * 
   * @param navigateToParent - Whether to navigate to parent folder (default: true)
   */
  closeViewer(navigateToParent: boolean = true): void {
    const currentParentId = this._currentParentId();
    
    this.logger.debug('🔒 Closing viewer');

    // Clear viewer state
    this._isViewerOpen.set(false);
    this._currentFileId.set(null);
    this._currentCategory.set(null);
    this._currentParentId.set(null);

    // Navigate back to parent if requested
    if (navigateToParent && currentParentId) {
      this.navigationStateService.updateCurrentSelectedItem(currentParentId);
    }
  }

  /**
   * Check if viewer is currently open
   */
  isOpen(): boolean {
    return this._isViewerOpen();
  }

  /**
   * Check if currently opening a viewer (to prevent navigation conflicts)
   */
  isCurrentlyOpeningViewer(): boolean {
    return this.isOpeningViewer;
  }

  /**
   * Check if viewer is open for a specific file
   */
  isOpenForFile(fileId: string): boolean {
    return this._isViewerOpen() && this._currentFileId() === fileId;
  }

  /**
   * Check if viewer is open for a specific category
   */
  isOpenForCategory(category: BizEntityTypeCategory): boolean {
    return this._isViewerOpen() && this._currentCategory() === category;
  }

  /**
   * Navigate to next file in the same folder (if applicable)
   * Returns true if navigation was successful
   */
  navigateToNextFile(currentFiles: IBizEntity[]): boolean {
    const currentFileId = this._currentFileId();
    const currentParentId = this._currentParentId();
    
    if (!currentFileId || !currentParentId) {
      return false;
    }

    // Find files in the same folder
    const siblingFiles = currentFiles.filter(f => 
      f.parentId === currentParentId && 
      !this.fileHandlerService.isNavigableContainer(f)
    );

    // Find current file index
    const currentIndex = siblingFiles.findIndex(f => f.id === currentFileId);
    
    if (currentIndex === -1 || currentIndex === siblingFiles.length - 1) {
      return false; // No next file
    }

    // Open next file
    const nextFile = siblingFiles[currentIndex + 1];
    return this.openViewer(nextFile);
  }

  /**
   * Navigate to previous file in the same folder (if applicable)
   * Returns true if navigation was successful
   */
  navigateToPreviousFile(currentFiles: IBizEntity[]): boolean {
    const currentFileId = this._currentFileId();
    const currentParentId = this._currentParentId();
    
    if (!currentFileId || !currentParentId) {
      return false;
    }

    // Find files in the same folder
    const siblingFiles = currentFiles.filter(f => 
      f.parentId === currentParentId && 
      !this.fileHandlerService.isNavigableContainer(f)
    );

    // Find current file index
    const currentIndex = siblingFiles.findIndex(f => f.id === currentFileId);
    
    if (currentIndex === -1 || currentIndex === 0) {
      return false; // No previous file
    }

    // Open previous file
    const previousFile = siblingFiles[currentIndex - 1];
    return this.openViewer(previousFile);
  }

  /**
   * Update query parameters for viewer state
   * Useful for maintaining state across browser refreshes
   */
  updateQueryParams(): void {
    const fileId = this._currentFileId();
    const parentId = this._currentParentId();

    if (fileId && parentId) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { 
          selectedItem: parentId,
          fileId: fileId 
        },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }

  /**
   * Clear query parameters
   */
  clearQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    });
  }

  /**
   * Reset viewer state completely
   */
  reset(): void {
    // this._isViewerOpen.set(false);
    this._currentFileId.set(null);
    this._currentCategory.set(null);
    this._currentParentId.set(null);
    // this.isOpeningViewer = false;
  }
}
